<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'quotes' }"
          >Quotes</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="my-6">
        <v-form ref="form" class="quote-form">
          <label class="text-field-label">Message</label>
          <v-textarea
            flat
            solo
            required
            class="mt-2"
            v-model="form.message"
            :error-messages="form.$getError('message')"
            counter
            maxlength="255"
            hide-details="auto"
            :loading="loading"
          ></v-textarea>

          <label class="text-field-label">Quotee</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.quotee"
            :error-messages="form.$getError('quotee')"
            :loading="loading"
          ></v-text-field>

          <div class="d-flex mt-4">
            <v-btn
              v-if="form.id"
              color="error"
              class="mr-4 px-6"
              height="40px"
              :loading="form.$busy"
              @click="deleteDialog = true"
              >Delete</v-btn
            >

            <v-spacer></v-spacer>

            <v-btn
              color="primary"
              class="px-6"
              height="40px"
              @click="quote ? updateQuoteDetails() : addQuote()"
              :loading="form.$busy"
              >Save</v-btn
            >
          </div>
        </v-form>

        <v-dialog v-model="deleteDialog" max-width="290">
          <v-card>
            <v-card-title class="headline">Confirm Delete</v-card-title>

            <v-card-text>Are you sure you want to delete this?</v-card-text>

            <v-card-actions>
              <v-btn text @click="deleteDialog = false" :loading="form.$busy"
                >Cancel</v-btn
              >

              <v-spacer></v-spacer>

              <v-btn color="red" text @click="removeQuote" :loading="form.$busy"
                >Yes, Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiChevronLeft, mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'

import Form from '@/utils/form'

export default {
  name: 'QuoteDetails',

  components: {
    AppBarNavIcon,
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline,
      },
      loading: false,
      deleteDialog: false,
      form: new Form({
        message: '',
        quotee: '',
      }),
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
      showModal: false,
    }
  },

  computed: {
    ...mapState({
      quote: (state) => state.quote.quoteDetails,
      authUser: (state) => state.auth.user,
    }),
    displayName() {
      return this.form.quote_name
    },
  },

  methods: {
    ...mapActions({
      getQuoteDetails: 'quote/getQuoteDetails',
      createQuote: 'quote/createQuote',
      updateQuote: 'quote/updateQuote',
      deleteQuote: 'quote/deleteQuote',
    }),

    ...mapMutations({
      clearQuoteDetails: 'quote/clearQuoteDetails',
    }),

    async getQuote() {
      this.loading = true
      await this.getQuoteDetails(this.$route.params.id)
      this.form = new Form(this.quote)
      this.loading = false
    },

    async updateQuoteDetails() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.updateQuote(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('Quote details successfully updated!', 'success')
          this.$router.replace({ name: 'quotes' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    async addQuote() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.createQuote(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('Quote successfully added!', 'success')
          this.$router.replace({ name: 'quotes' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    removeQuote() {
      this.form.$busy = true
      this.deleteQuote(this.form.id)
        .then(() => {
          this.form.$busy = false
          this.deleteDialog = false
          this.showSnackbar('Quote deleted successfully!')
          this.$router.replace('/quotes')
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 404) {
            this.deleteDialog = false
            this.showSnackbar(
              'The quote you are trying to delete is no longer existing.',
              'error'
            )
          }
        })
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },

  created() {
    if (this.$router.currentRoute.name === 'quote.details') {
      this.getQuote()
    }
  },

  destroyed() {
    this.clearQuoteDetails()
  },

  watch: {
    $route() {
      this.getQuote()
    },
  },
}
</script>

<style lang="scss" scoped>
.quote-form {
  width: 400px;

  @media (max-width: 420px) {
    width: 100%;
  }
}
</style>
